import type { Features } from '@/config/types.config';
import { isFeatureEnabled, requiresPermissionCheck, type FeatureContext } from '@/feature/feature';
import { useCoworkerPermissions } from '@repo/hooks';
import { getAppConfig } from '@/utils/window.utils';

interface TimeWindowFeatureContext extends FeatureContext {
    hasDeliveryServices: boolean;
    hasProvidedServices: boolean;
}

type TimeWindowCategory = Exclude<
    keyof NonNullable<NonNullable<Features['services']>['timeWindowSelection']>,
    'disabledServiceIds'
>;

const isTimeWindowSelectionEnabled = (category: TimeWindowCategory, context: TimeWindowFeatureContext): boolean => {
    const feature = getAppConfig().feature.services?.timeWindowSelection?.[category];
    const hasServices = category === 'delivery' ? context.hasDeliveryServices : context.hasProvidedServices;

    return hasServices && isFeatureEnabled(feature, context);
};

export const useIsTimeWindowSelectionEnabled = (
    category: TimeWindowCategory,
    context: TimeWindowFeatureContext,
    coworkerBaseUrl: string,
    buCode: string
): boolean => {
    const config = getAppConfig();
    const feature = config.feature.services?.timeWindowSelection?.[category];
    const hasServices = category === 'delivery' ? context.hasDeliveryServices : context.hasProvidedServices;
    const shouldFetchCoworkerPermissions = hasServices && requiresPermissionCheck(feature, context);
    const { data: permissionsData } = useCoworkerPermissions(coworkerBaseUrl, buCode, shouldFetchCoworkerPermissions);

    return isTimeWindowSelectionEnabled(category, {
        ...context,
        permissions: permissionsData?.authorizations,
    });
};
